import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import type { ApiResponse } from '@/types'

const API_PREFIX = import.meta.env.VITE_API_BASE_URL || '/api'

// ================== CSRF Token 管理（Frappe 会话防护） ==================
// Desk 一旦启动会给会话生成 csrf_token，SPA 不带 X-Frappe-CSRF-Token 的
// POST 会全部 403（无效请求）→ 前端拦截器误判为掉登录。这里统一注入 token，
// 遇到 CSRFTokenError 时先取 token 重试一次，再判断是否真的需要跳登录。
let csrfToken = ''
let csrfFetchPromise: Promise<string | null> | null = null

export function setCsrfToken(token: string) {
  if (token) csrfToken = token
}

/** 会话失效判定：Frappe 16 实测两种 403 文案——
 * ① 会话过期（lastupdate 超过 site_config session_expiry=28800 即 8h，212/211 同值）：
 *    Guest 撞 allow_guest=False →
 *    "您的登录已过期或尚未登录，请重新登录方法 …申明前未添加@frappe.whitelist()装饰器"
 *    （2026-10-14 实测 455 字节；修复前正则匹配不到，用户只看到
 *    "Request failed with status code 403" 错误条且每 30s 被 notify 轮询重播）；
 * ② 未登录直连受保护方法："您无权访问此资源。请登录后访问方法…"（2026-08-07 实测 440 字节）。
 * 真·角色权限不足返回 "无权限：…"（unified 接口捕获后为 HTTP 200+code 403），不跳转。 */
function isSessionExpired(excType: string, msg: string): boolean {
  if (excType === 'AuthenticationError') return true
  return excType === 'PermissionError' && /请登录|无权访问此资源|登录已过期|尚未登录|请重新登录/.test(msg)
}

/** GET 安全方法获取当前会话的 CSRF token（后端 get_csrf_token 自动生成） */
function fetchCsrfToken(): Promise<string | null> {
  if (!csrfFetchPromise) {
    csrfFetchPromise = (async () => {
      try {
        const res: any = await instance.get('/method/custom_app.api.unified.get_csrf_token', {
          params: { _t: Date.now() },
          // token 预取请求：未登录时 403 属正常，禁止触发会话失效跳转（否则登录页无限刷新）
          _skipAuthRedirect: true,
        } as any)
        const token = res?.message || res?.csrf_token || ''
        if (typeof token === 'string' && token.length >= 8) {
          csrfToken = token
          return token
        }
      } catch {
        // 未登录等场景忽略，交由 401/403 统一处理
      }
      return null
    })().finally(() => {
      csrfFetchPromise = null
    })
  }
  return csrfFetchPromise
}

/** 应用启动时预取 CSRF token：已登录用户刷新/直达页面时 csrfToken 内存为空，
 *  首屏并发 POST 会因无 token 触发 CSRFTokenError 400。挂载前先取好可彻底消除该竞态。 */
export function ensureCsrfToken(): Promise<string | null> {
  return fetchCsrfToken()
}

const instance: AxiosInstance = axios.create({
  baseURL: API_PREFIX,
  timeout: 30000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

instance.interceptors.request.use((config) => {
  // 非安全方法注入 CSRF token（与 Desk 行为一致）
  const method = (config.method || 'get').toLowerCase()
  if (csrfToken && !['get', 'head', 'options', 'trace'].includes(method)) {
    config.headers = config.headers || {}
    config.headers['X-Frappe-CSRF-Token'] = csrfToken
  }
  return config
})

// ================== 响应拦截 ==================
instance.interceptors.response.use(
  (response) => {
    const data = response.data
    // ERPNext API 响府包裹在 message 字段中
    if (data && typeof data.message === 'object' && data.message !== null) {
      const unwrapped = { ...data.message }
      // 后端错误时 message 常为空串，真实信息在 _error_message / _server_messages 里
      if (!unwrapped.message) {
        if (data._error_message) {
          unwrapped.message = data._error_message
        } else if (data._server_messages) {
          try {
            const parsed = JSON.parse(data._server_messages)
            if (parsed[0]) unwrapped.message = JSON.parse(parsed[0]).message || data._error_message || ''
          } catch {}
        }
      }
      return { ...unwrapped, _raw: data }
    }
    // ERPNext 权限未登录错误
    if (data && data.exc_type) {
      let msg = data.exc_type
      try {
        if (data._server_messages) {
          const parsed = JSON.parse(data._server_messages)
          msg = JSON.parse(parsed[0]).message || msg
        }
      } catch {}
      // 仅会话失效（未登录 / 会话过期）跳登录；真·无权限只 reject 提示，
      // 避免上传等接口因权限不足返回 403 被误判为掉登录（2026-08-07 实测）。
      if (isSessionExpired(data.exc_type, msg)) {
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        window.location.replace('/login')
      }
      return Promise.reject(new Error(msg))
    }
    return data
  },
  async (error) => {
    const status = error.response?.status
    const body = error.response?.data || {}
    // CSRF 校验失败：取 token 后自动重试一次（同一会话被 Desk 启动过就会触发）
    // Frappe 对 CSRFTokenError 的 http_status_code 为 400，个别版本/路径也可能返回 403
    if (
      (status === 400 || status === 403) &&
      (body.exc_type === 'CSRFTokenError' || body.exc_type === 'CSRFTokenMismatchError') &&
      !(error.config as any)?._csrfRetried
    ) {
      const token = await fetchCsrfToken()
      if (token) {
        const retryConfig = {
          ...error.config,
          _csrfRetried: true,
          headers: {
            ...(error.config?.headers || {}),
            'X-Frappe-CSRF-Token': token,
          },
        }
        return instance.request(retryConfig)
      }
    }
    // 401 = 未认证 → 跳登录；403 时区分“未登录/会话过期”（跳登录）与
    // “真·无权限”（仅提示，避免误踢；unified 接口的无权限走 HTTP 200+code 403）。
    let bodyMsg = body.exc_type || ''
    try {
      if (body._server_messages) {
        const parsed = JSON.parse(body._server_messages)
        bodyMsg = JSON.parse(parsed[0]).message || bodyMsg
      }
    } catch {}
    // 没有 _server_messages 时从 exception 文本取业务消息（形如
    // "frappe.exceptions.ValidationError: 当前工作流状态「None」不允许提交企微审批"）。
    // 不取的话调用方只能显示 axios 的 "Request failed with status code 417"。
    if (!bodyMsg || bodyMsg === (body.exc_type || '')) {
      const rawExc = typeof body.exception === 'string' ? body.exception : ''
      const sep = rawExc.indexOf(': ')
      if (sep > 0) bodyMsg = rawExc.slice(sep + 2)
      else if (body._error_message) bodyMsg = body._error_message
    }
    // 预取 CSRF token 的请求在未登录时返回 403 属正常现象，须跳过会话失效跳转，
    // 否则 main.ts 启动预取会把页面带入"403→跳 /login→重载→403"的无限刷新循环（桌面版/网页版都会闪）
    const skipAuthRedirect = !!(error.config as any)?._skipAuthRedirect
    const isAuthFailure = (status === 401 || isSessionExpired(body.exc_type, bodyMsg)) && !skipAuthRedirect
    if (isAuthFailure) {
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      window.location.replace('/login')
    }
    if (status === 404) {
      console.warn('[API 404]', error.config?.url)
    }
    // 把后端业务提示带回给调用方；仍 reject axios 原对象，.response / status 不受影响。
    if (bodyMsg && bodyMsg !== (body.exc_type || '')) error.message = bodyMsg
    return Promise.reject(error)
  }
)

export function request<T = any>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
  return instance.request(config)
}

export function validateFields(returned: Record<string,any>, expected: string[], label: string) {
  const missing = expected.filter(k => !(k in returned))
  if (missing.length > 0) {
    console.warn(`[field missing] ${label}:`, missing.join(', '))
  }
}

export function validateRequired(form: Record<string,any>, required: string[], labels: Record<string,string> = {}): string[] {
  const errors: string[] = []
  required.forEach(k => {
    const v = form[k]
    if (v === undefined || v === null || v === '') {
      errors.push(labels[k] || k)
    }
  })
  return errors
}

export default instance
